import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { usePrefersReducedMotion } from "./motion";
import { FlowGlowDefs, FlowPath } from "./flow";

/*
 * "Vulnerability scan chain" diagram - ported from the product presentation.
 * Asset → detected software → opsanio CVE-Abgleich (fed by CVE sources) →
 * concretely affected systems with CVSS scores. The source pills mirror what
 * opsanio actually ingests (go-threats: NVD, MITRE, CISA KEV, ENISA EUVD, OSV,
 * Microsoft MSRC, Red Hat / SUSE CSAF-VEX, Debian; BSI CERT-Bund WID via the
 * RSS scanner and vendor search) - keep them in sync when sources change. Themed via navy-band tokens so it
 * works in both light and dark; flow packets travel along the connectors and are
 * dropped under reduced motion (see ./flow).
 */

// Layout (same as the brag video): CVE-source panel on top, three connectors from
// its bottom edge into the hub; asset/software rows, hub and affected systems share
// one vertical centre line (CY); every connector runs box edge to box edge.
const VB = { w: 1040, h: 350 };
const AX = 20;
const AW = 150;
const SX = 200;
const SW = 150;
const HUBX = 450;
const HUBW = 140;
const CY = 240; // common centre line
const HUBY = CY;
const HUBH = 80;
const EX = 760;
const EW = 260;
const ROW_H = 40;
const ROW_Y = [165, 215, 265, 315]; // 4 rows, block 145-335, centre 240
const AFF_H = 44;
const AFF_Y = [167, 240, 313]; // flush with the row block, middle on CY
const PANEL = { x: 300, y: 8, w: 440, h: 95 }; // CVE sources, 12 px padding
const PILL_W = 76;
const PILL_H = 24;
const PILL_GAP = 9;
const PER_ROW = 5;
const LABEL_Y = 130;

const ICO = {
  asset: "M8 2h8v4H8zM6 6h12a1 1 0 011 1v13a1 1 0 01-1 1H6a1 1 0 01-1-1V7a1 1 0 011-1zM9 12h6M9 16h6",
  pkg: "M12 2l8 4v12l-8 4-8-4V6zM4 6l8 4 8-4M12 10v12",
  alert: "M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM12 9v3M12 15h.01",
};


export const AutonubilVulnChain: React.FC = () => {
  const { translate } = useAnketaI18n();
  const tp = "anketa.autonubil.home.vuln";
  const t = (k: string, d: string) => translate(`${tp}.${k}`, { defaultMessage: d } as any);
  const reduced = usePrefersReducedMotion();

  const assets = ["Exchange-Server", "Web-Frontend", "App-Server", "Client-Pool"];
  const soft = ["Exchange 2019", "OpenSSL 1.1.1", "Apache 2.4", "Log4j 2.14"];
  const cveq = ["NVD", "MITRE", "BSI", "EUVD", "CISA KEV", "OSV", "Microsoft", "Red Hat", "SUSE", "Debian"];
  const aff: Array<[string, string, string]> = [
    ["Exchange-Server", "CVE-2023-23397", "9.8"],
    ["App-Server", "CVE-2024-3400", "9.3"],
    ["Web-Frontend", "CVE-2024-21887", "8.2"],
  ];
  const ay = ROW_Y;
  const sy = ROW_Y;
  const ey = AFF_Y;
  const pillX = (i: number) => PANEL.x + 12 + (i % PER_ROW) * (PILL_W + PILL_GAP);
  const pillY = (i: number) => PANEL.y + 26 + Math.floor(i / PER_ROW) * (PILL_H + PILL_GAP);
  const hubTop = HUBY - HUBH / 2;
  const panelBottom = PANEL.y + PANEL.h;
  const feedX = [HUBX + 40, HUBX + HUBW / 2, HUBX + HUBW - 40]; // panel -> hub connectors
  const inY = sy.map((_, i) => HUBY - 18 + i * 12); // where software lines enter the hub (left edge)
  const outY = ey.map((_, i) => HUBY - 12 + i * 12); // where affected lines leave the hub (right edge)

  return (
    <div
      style={{ width: "100%" }}
      role="img"
      aria-label={t(
        "aria",
        "opsanio detects installed software from assets, matches it against CVE sources (NVD, MITRE, BSI CERT-Bund, ENISA EUVD, CISA KEV, OSV and vendor advisories from Microsoft, Red Hat, SUSE and Debian) and shows which systems are concretely affected, with CVSS scores.",
      )}
    >
      <svg
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", overflow: "visible" }}
      >
        <FlowGlowDefs id="vulnGlow" />
        {/* column labels */}
        <text x={AX} y={LABEL_Y} fill="var(--ak-accent-2)" fontSize={11} fontWeight={700} letterSpacing="0.12em">
          1 · {t("col.assets", "ASSETS")}
        </text>
        <text x={SX} y={LABEL_Y} fill="var(--ak-accent-2)" fontSize={11} fontWeight={700} letterSpacing="0.12em">
          2 · {t("col.software", "SOFTWARE")}
        </text>
        <text x={EX} y={LABEL_Y} fill="#ffb3ba" fontSize={11} fontWeight={700} letterSpacing="0.12em">
          4 · {t("col.affected", "BETROFFEN")}
        </text>

        {/* flows: packets travel asset → software → hub → affected; sources feed the hub */}
        {ay.map((y, i) => (
          <FlowPath
            key={`as-${i}`}
            id={`vc-as-${i}`}
            glowId="vulnGlow"
            reduced={reduced}
            dur={2.8 + (i % 3) * 0.4}
            d={`M${AX + AW},${y} L${SX},${sy[i]}`}
          />
        ))}
        {sy.map((y, i) => (
          <FlowPath
            key={`sh-${i}`}
            id={`vc-sh-${i}`}
            glowId="vulnGlow"
            reduced={reduced}
            dur={3 + (i % 3) * 0.4}
            d={`M${SX + SW},${y} C${SX + SW + 50},${y} ${HUBX - 50},${inY[i]} ${HUBX},${inY[i]}`}
          />
        ))}
        {feedX.map((x, i) => (
          <FlowPath
            key={`qh-${i}`}
            id={`vc-qh-${i}`}
            glowId="vulnGlow"
            reduced={reduced}
            dur={2.4 + i * 0.3}
            d={`M${x},${panelBottom} L${x},${hubTop}`}
          />
        ))}
        {ey.map((y, i) => (
          <FlowPath
            key={`he-${i}`}
            id={`vc-he-${i}`}
            glowId="vulnGlow"
            reduced={reduced}
            color="var(--ak-sev-critical)"
            dur={2.8 + (i % 3) * 0.4}
            d={`M${HUBX + HUBW},${outY[i]} C${HUBX + HUBW + 60},${outY[i]} ${EX - 60},${y} ${EX},${y}`}
          />
        ))}

        {/* asset nodes */}
        {assets.map((a, i) => (
          <g key={a}>
            <rect x={AX} y={ay[i] - ROW_H / 2} width={AW} height={ROW_H} rx={10} fill="var(--ak-navy-2)" stroke="var(--ak-accent-border)" strokeWidth={1.2} />
            <path d={ICO.asset} transform={`translate(${AX + 10}, ${ay[i] - 9}) scale(0.75)`} fill="none" stroke="var(--ak-accent-2)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            <text x={AX + 34} y={ay[i] + 4} fill="var(--ak-on-navy)" fontWeight={600} fontSize={11.5}>{a}</text>
          </g>
        ))}
        {/* software nodes */}
        {soft.map((a, i) => (
          <g key={a}>
            <rect x={SX} y={sy[i] - ROW_H / 2} width={SW} height={ROW_H} rx={10} fill="var(--ak-navy-2)" stroke="var(--ak-accent-border)" strokeWidth={1.2} />
            <path d={ICO.pkg} transform={`translate(${SX + 10}, ${sy[i] - 9}) scale(0.75)`} fill="none" stroke="var(--ak-accent-2)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            <text x={SX + 34} y={sy[i] + 4} fill="var(--ak-on-navy)" fontWeight={600} fontSize={11.5}>{a}</text>
          </g>
        ))}

        {/* hub */}
        {/* CVE source panel (top) */}
        <rect x={PANEL.x} y={PANEL.y} width={PANEL.w} height={PANEL.h} rx={14} fill="var(--ak-navy-3)" stroke="var(--ak-accent-border)" strokeWidth={1.2} />
        <text x={PANEL.x + 12} y={PANEL.y + 17} fill="var(--ak-accent-2)" fontSize={11} fontWeight={700} letterSpacing="0.12em">
          3 · {t("col.sources", "CVE-QUELLEN")}
        </text>
        {cveq.map((c, i) => (
          <g key={c}>
            <rect
              x={pillX(i)}
              y={pillY(i)}
              width={PILL_W}
              height={PILL_H}
              rx={12}
              fill="var(--ak-navy-2)"
              stroke="var(--ak-accent-border)"
              strokeWidth={1.2}
            />
            <text x={pillX(i) + PILL_W / 2} y={pillY(i) + 16} textAnchor="middle" fill="var(--ak-on-navy)" fontWeight={600} fontSize={10.5}>
              {c}
            </text>
          </g>
        ))}

        <rect x={HUBX} y={hubTop} width={HUBW} height={HUBH} rx={14} fill="var(--ak-navy-3)" stroke="var(--ak-accent-border)" strokeWidth={1.4} />
        <text x={HUBX + HUBW / 2} y={HUBY - 2} textAnchor="middle" fill="var(--ak-on-navy)" fontFamily="Manrope, Inter, sans-serif" fontWeight={800} fontSize={18}>opsanio</text>
        <text x={HUBX + HUBW / 2} y={HUBY + 18} textAnchor="middle" fill="var(--ak-accent-2)" fontWeight={600} fontSize={9} letterSpacing="0.18em">CVE-ABGLEICH</text>

        {/* affected (red) */}
        {aff.map((a, i) => {
          const y = ey[i];
          return (
            <g key={a[0]}>
              <rect x={EX} y={y - AFF_H / 2} width={EW} height={AFF_H} rx={11} fill="rgba(255,93,108,0.08)" stroke="rgba(255,93,108,0.4)" strokeWidth={1.2} />
              <path d={ICO.alert} transform={`translate(${EX + 12}, ${y - 10}) scale(0.8)`} fill="none" stroke="#ff97a1" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
              <text x={EX + 42} y={y - 2} fill="var(--ak-on-navy)" fontWeight={700} fontSize={12}>{a[0]}</text>
              <text x={EX + 42} y={y + 13} fill="var(--ak-on-navy-muted)" fontSize={10}>{a[1]}</text>
              <text x={EX + EW - 14} y={y + 5} textAnchor="end" fill="#ff6b78" fontWeight={800} fontSize={15}>{a[2]}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default AutonubilVulnChain;
